import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { SkipErrorReporting } from 'tio-data/request-options';

/**
 * Request builders for tio_types.settings (api/v1, mounted at the host root).
 *
 * A setting row is a polymorphic-owner triple: <owner> has <predicate> of
 * <value> for <subject>. Per-admin report preferences are stored owned by the
 * admin's User.
 *
 * Casing gotcha: the GET filter takes the MODEL class name
 * (`filter[owner_type]=User`) while the upsert payload's owner relationship
 * takes the JSONAPI RESOURCE type (`type: 'users'`).
 *
 * No `setting` schema is registered with the store, so every request skips
 * the cache and callers read the raw JSON:API document from `content`.
 */

/** One JSON:API settings resource as returned by the settings endpoints. */
export interface SettingResource {
  id: string;
  type?: string;
  attributes?: {
    subject?: string;
    predicate?: string;
    value?: Record<string, unknown>;
  };
}

function baseRequest(path: string): RequestInfo {
  return {
    url: buildBaseURL({ resourcePath: path }),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
    // Settings CRUD is super-admin gated and reports-preferences degrades
    // silently on 403 — without this, the request manager's global toast
    // ("Access denied") fires before the service can swallow the error.
    options: {
      [SkipErrorReporting]: true,
    },
  };
}

export function querySettingsRequest(params: {
  ownerType: string;
  ownerId: string;
  subject: string;
  predicate: string;
}): RequestInfo {
  const request = baseRequest('settings');
  const query = new URLSearchParams({
    'filter[owner_type]': params.ownerType,
    'filter[owner_id]': params.ownerId,
    'filter[subject]': params.subject,
    'filter[predicate]': params.predicate,
  });
  request.url = `${request.url}?${query.toString()}`;
  return request;
}

export function upsertSettingRequest(params: {
  ownerResourceType: string;
  ownerId: string;
  subject: string;
  predicate: string;
  value: Record<string, unknown>;
}): RequestInfo {
  const request = baseRequest('settings/upsert');
  request.method = 'PUT';
  request.body = JSON.stringify({
    data: {
      type: 'settings',
      attributes: {
        subject: params.subject,
        predicate: params.predicate,
        value: params.value,
      },
      relationships: {
        owner: {
          data: { type: params.ownerResourceType, id: params.ownerId },
        },
      },
    },
  });
  return request;
}

/**
 * Wholesale value replacement. PATCH /settings/:id deep-merges and 409s on
 * array-length mismatch, so array-valued preferences must use replace.
 */
export function replaceSettingValueRequest(
  id: string,
  value: Record<string, unknown>
): RequestInfo {
  const request = baseRequest(`settings/${id}/replace`);
  request.method = 'PUT';
  request.body = JSON.stringify({
    data: { type: 'settings', id, attributes: { value } },
  });
  return request;
}
